'use client';

import { IconEye, IconEyeInvisible } from '@/components/common/icons/SVGs';
import { Button } from '@/components/common/inputs/button/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/common/inputs/text/form';
import { Checkbox } from '@/components/common/inputs/checkbox/checkbox';
import { Input } from '@/components/common/inputs/text/input';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/common/layout/card';
import { ConfirmationDialog } from '@/components/features/core/auth/ConfirmationDialog';
import {
  Language,
  LanguageSelector,
} from '@/components/features/i18n/LanguageSelector';
import { APP_CONFIG } from '@/lib/config/api.config';
import { CDN_BASE_URL } from '@/lib/constants/cdn';
import { AuthenticationError } from '@/lib/errors/auth.errors';
import { useMetrics, usePageViewTracking } from '@/lib/hooks/useMetrics';
import { authService } from '@/lib/services';
import { MetricsAction, MetricsCategory } from '@/lib/utils/metrics.utils';
import { useI18n } from '@/locales/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { signIn } from 'next-auth/react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

export default function Registration() {
  const t = useI18n();
  const router = useRouter();
  const [currentStep, setCurrentStep] = useState(1);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [credentials, setCredentials] =
    useState<RegistrationFormSchemaType | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);
  const { trackEvent } = useMetrics();
  const { ROUTES } = APP_CONFIG;
  // Page view tracking removed as per request
  // usePageViewTracking('Registration Page');

  const handleConfirmationSuccess = async () => {
    if (credentials) {
      try {
        // Check if step 2 data exists
        const step2DataStr = sessionStorage.getItem('registrationStep2Data');
        let step2Data = null;
        if (step2DataStr) {
          try {
            step2Data = JSON.parse(step2DataStr);
          } catch (e) {
            console.error('Error parsing step 2 data:', e);
            // Track error parsing step 2 data
            trackEvent({
              category: MetricsCategory.ERROR,
              action: MetricsAction.CLIENT_ERROR,
              label: 'Registration Step 2 Data Parse Error',
              value: 0,
              dimensions: {
                error: e instanceof Error ? e.message : 'Unknown error',
              },
            });
          }
        }

        // If step 2 data doesn't exist, set current step to 2 and return
        if (!step2Data) {
          setCurrentStep(2);
          return;
        }

        // Track login attempt after registration
        trackEvent({
          category: MetricsCategory.USER_ACTIVITY,
          action: MetricsAction.LOGIN,
          label: 'Post-Registration Login Attempt',
          value: 0,
          dimensions: { email: credentials.email },
        });

        const signInResult = await signIn('credentials', {
          email: credentials.email,
          password: credentials.password,
          redirect: false,
          callbackUrl: `/${credentials.language || 'en'}/`,
        });

        if (signInResult?.error) {
          // Track login error after registration
          trackEvent({
            category: MetricsCategory.ERROR,
            action: MetricsAction.CLIENT_ERROR,
            label: 'Post-Registration Login Failed',
            value: 0,
            dimensions: { error: signInResult.error },
          });
          throw new Error(signInResult.error);
        }

        if (signInResult?.ok) {
          // Track successful login after registration
          trackEvent({
            category: MetricsCategory.USER_ACTIVITY,
            action: MetricsAction.LOGIN,
            label: 'Post-Registration Login Success',
            value: 0,
            dimensions: { email: credentials.email },
          });
          router.push(signInResult.url || `/${credentials.language || 'en'}/`);
        } else {
          throw new Error('Failed to sign in after confirmation');
        }
      } catch (error) {
        setError('Failed to sign in after confirmation');
        // Track error during post-registration login
        trackEvent({
          category: MetricsCategory.ERROR,
          action: MetricsAction.CLIENT_ERROR,
          label: 'Post-Registration Login Exception',
          value: 0,
          dimensions: {
            error: error instanceof Error ? error.message : 'Unknown error',
          },
        });
      }
    }
  };

  const RegisterFormSchemaStep1 = z
    .object({
      name: z.string().min(2, t('register.errors.name')),
      email: z.string().email(t('register.errors.email')),
      password: z
        .string()
        .min(8, t('register.errors.passwordLength'))
        .regex(/[A-Z]/, t('register.errors.passwordUppercase'))
        .regex(/[a-z]/, t('register.errors.passwordLowercase'))
        .regex(/[0-9]/, t('register.errors.passwordNumber'))
        .regex(/[!@#$%^&*(),.?":{}|<>]/, t('register.errors.passwordSpecial')),
      repeatPassword: z.string(),
    })
    .refine((data) => data.password === data.repeatPassword, {
      message: t('register.errors.passwordMatch'),
      path: ['repeatPassword'],
    });

  // Step 2 Schema
  const RegisterFormSchemaStep2 = z.object({
    city: z.string().nonempty('City is required'),
    ageGroup: z.enum(['18-25', '26-35', '36-50', '51+']),
    language: z.string().min(1, 'Please select a language'),
    role: z.enum(['user', 'lawyer']),
    acceptedPolicies: z.literal(true, {
      errorMap: () => ({
        message: 'You must accept the terms and conditions.',
      }),
    }),
  });

  type RegisterFormSchemaTypeStep1 = z.infer<typeof RegisterFormSchemaStep1>;
  type RegisterFormSchemaTypeStep2 = z.infer<typeof RegisterFormSchemaStep2>;
  type RegistrationFormSchemaType = RegisterFormSchemaTypeStep1 &
    RegisterFormSchemaTypeStep2;

  const formStep1 = useForm<RegisterFormSchemaTypeStep1>({
    resolver: zodResolver(RegisterFormSchemaStep1),
    defaultValues: {
      name: '',
      email: '',
      password: '',
      repeatPassword: '',
    },
  });

  const formStep2 = useForm<RegisterFormSchemaTypeStep2>({
    resolver: zodResolver(RegisterFormSchemaStep2),
    defaultValues: {
      city: '',
      ageGroup: undefined,
      language: '',
      role: localStorage.getItem('selectedCard') as 'user' | 'lawyer',
      acceptedPolicies: undefined,
    },
  });

  async function handleSubmitStep1(
    values: z.infer<typeof RegisterFormSchemaStep1>
  ) {
    // Track registration step 1 completion
    trackEvent({
      category: MetricsCategory.USER_ACTIVITY,
      action: MetricsAction.SIGNUP,
      label: 'Registration Step 1 Completed',
      value: 0,
      dimensions: { email: values.email },
    });

    // You can save this data to local state or context for the second step.
    console.log('Step 1 Data:', values);
    setCurrentStep(2);
  }

  async function handleSubmitStep2(
    values: z.infer<typeof RegisterFormSchemaStep2>
  ) {
    console.log('Step 2 Data:', values);
    setIsLoading(true);
    setError(null);

    try {
      // Track registration step 2 submission
      trackEvent({
        category: MetricsCategory.USER_ACTIVITY,
        action: MetricsAction.SIGNUP,
        label: 'Registration Step 2 Submitted',
        value: 0,
        dimensions: {
          language: values.language,
          ageGroup: values.ageGroup,
        },
      });

      // Store step 2 data in session storage
      sessionStorage.setItem(
        'registrationStep2Data',
        JSON.stringify({
          city: values.city,
          ageGroup: values.ageGroup,
          language: values.language,
          acceptedPolicies: values.acceptedPolicies,
        })
      );

      const registerData = {
        name: formStep1.getValues().name,
        email: formStep1.getValues().email.toLowerCase(),
        password: formStep1.getValues().password,
        userType: 'email',
        city: values.city,
        language: values.language,
        ageGroup: values.ageGroup,
        acceptedPolicies: values.acceptedPolicies,
        avatar: '',
        role: values.role,
      };

      console.log('Register Data:', registerData);

      // Register with our backend API
      const result = await authService.register(registerData);

      // If user exists, show message and redirect to login
      if (result.userExists) {
        // Track user already exists
        trackEvent({
          category: MetricsCategory.USER_ACTIVITY,
          action: MetricsAction.SIGNUP,
          label: 'Registration User Already Exists',
          value: 0,
          dimensions: { email: registerData.email },
        });

        setCurrentStep(1);
        formStep1.setError('root', {
          type: 'info',
          message: result.message,
        });
        return;
      }

      // Save credentials for confirmation
      setCredentials({
        ...formStep1.getValues(),
        ...values,
      });

      // Track successful registration
      trackEvent({
        category: MetricsCategory.USER_ACTIVITY,
        action: MetricsAction.SIGNUP,
        label: 'Registration Success',
        value: 0,
        dimensions: {
          email: registerData.email,
          language: values.language,
          ageGroup: values.ageGroup,
        },
      });

      // Show success message if provided
      if (result.message) {
        formStep2.setError('root', {
          type: result.isResend ? 'info' : 'success',
          message: result.message,
        });
      }

      // Show confirmation dialog if needed
      if (result.needsConfirmation) {
        // Track confirmation needed
        trackEvent({
          category: MetricsCategory.USER_ACTIVITY,
          action: MetricsAction.SIGNUP,
          label: 'Registration Confirmation Needed',
          value: 0,
          dimensions: { email: registerData.email },
        });

        setShowConfirmation(true);
      }
    } catch (err) {
      console.error('Registration error:', err);
      let errorMessage = 'Registration failed. Please try again.';
      let errorType = 'error';
      let errorCode = 'UNKNOWN_ERROR';

      if (err instanceof AuthenticationError) {
        errorCode = err.code;
        switch (err.code) {
          case 'INVALID_PASSWORD':
            errorMessage = 'Password does not meet requirements.';
            break;
          default:
            errorMessage = err.message;
        }
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }

      // Track registration error
      trackEvent({
        category: MetricsCategory.ERROR,
        action: MetricsAction.CLIENT_ERROR,
        label: 'Registration Failed',
        value: 0,
        dimensions: {
          error: errorMessage,
          errorCode: errorCode,
        },
      });

      formStep2.setError('root', {
        type: errorType,
        message: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  }

  const [selectedLanguage, setSelectedLanguage] = useState<Language | null>(
    null
  );

  const handleSelectLanguage = (language: Language) => {
    setSelectedLanguage(language);
    formStep2.setValue('language', language.key); // Update form with single language
  };

  // Add password requirements display
  const PasswordRequirement = ({
    met,
    label,
  }: {
    met: boolean;
    label: string;
  }) => (
    <div className='flex items-center gap-2 text-sm'>
      <div
        className={`h-2 w-2 rounded-full ${met ? 'bg-green-500' : 'bg-muted-foreground/30'}`}
      />
      <span className={met ? 'text-green-700' : 'text-muted-foreground'}>
        {label}
      </span>
    </div>
  );

  // Replace text-gray-700 with text-foreground throughout this component

  return (
    <>
      <Card className='mx-auto w-full max-w-[600px] border-none bg-transparent px-4 shadow-none dark:bg-background sm:px-0'>
        <CardHeader className='space-y-2 text-center'>
          <div className='flex flex-col items-center'>
            <div className='relative h-[111px] w-[111px]'>
              <Image
                fill
                sizes='(max-width: 111px) 100vw, 111px'
                style={{ objectFit: 'cover' }}
                alt='Jaqln Logo'
                priority
                src={`${CDN_BASE_URL}/JAQLNLogo.png`}
              />
            </div>
            <CardTitle className='text-3xl font-bold'>
              {t('register.title')}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {currentStep === 1 && (
            <Form {...formStep1}>
              <form onSubmit={formStep1.handleSubmit(handleSubmitStep1)}>
                {formStep1.formState.errors.root && (
                  <div
                    className={`mb-4 text-sm ${
                      formStep1.formState.errors.root.type === 'success'
                        ? 'text-green-600'
                        : formStep1.formState.errors.root.type === 'info'
                          ? 'text-blue-600'
                          : 'text-red-500'
                    }`}
                  >
                    {formStep1.formState.errors.root.message}
                  </div>
                )}
                <div className='grid w-full items-center gap-4'>
                  <FormField
                    control={formStep1.control}
                    name='name'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className='text-base font-medium text-foreground'>
                          {t('register.name')}
                        </FormLabel>
                        <FormControl>
                          <Input
                            className='h-12 rounded-lg focus:ring-2 focus:ring-primary'
                            {...field}
                          />
                        </FormControl>
                        <FormMessage className='text-xs' />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={formStep1.control}
                    name='email'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className='text-base font-medium text-foreground'>
                          {t('register.email')}
                        </FormLabel>
                        <FormControl>
                          <Input
                            type='email'
                            autoComplete='email'
                            className='h-12 rounded-lg focus:ring-2 focus:ring-primary'
                            {...field}
                          />
                        </FormControl>
                        <FormMessage className='text-xs' />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={formStep1.control}
                    name='password'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className='text-base font-medium text-foreground'>
                          {t('register.password')}
                        </FormLabel>
                        <FormControl>
                          <div className='space-y-2'>
                            <div className='relative'>
                              <Input
                                autoComplete='new-password'
                                className='h-12 rounded-lg pr-12 focus:ring-2 focus:ring-primary'
                                type={showPassword ? 'text' : 'password'}
                                {...field}
                              />
                              <button
                                type='button'
                                onClick={() => setShowPassword(!showPassword)}
                                className='absolute right-3 top-1/2 -translate-y-1/2'
                              >
                                {showPassword ? (
                                  <IconEyeInvisible
                                    color='rgb(124 58 237)'
                                    height={24}
                                    width={24}
                                  />
                                ) : (
                                  <IconEye
                                    color='rgb(124 58 237)'
                                    height={24}
                                    width={24}
                                  />
                                )}
                              </button>
                            </div>
                          </div>
                        </FormControl>
                        <FormMessage className='text-xs' />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={formStep1.control}
                    name='repeatPassword'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className='text-base font-medium text-foreground'>
                          {t('register.passwordConfirm')}
                        </FormLabel>
                        <FormControl>
                          <div className='space-y-2'>
                            <div className='relative'>
                              <Input
                                autoComplete='new-password'
                                className='h-12 rounded-lg pr-12 focus:ring-2 focus:ring-primary'
                                type={showRepeatPassword ? 'text' : 'password'}
                                {...field}
                              />
                              <button
                                type='button'
                                onClick={() =>
                                  setShowRepeatPassword(!showRepeatPassword)
                                }
                                className='absolute right-3 top-1/2 -translate-y-1/2'
                              >
                                {showRepeatPassword ? (
                                  <IconEyeInvisible
                                    color='currentColor'
                                    className='text-primary'
                                    height={24}
                                    width={24}
                                  />
                                ) : (
                                  <IconEye
                                    color='currentColor'
                                    className='text-primary'
                                    height={24}
                                    width={24}
                                  />
                                )}
                              </button>
                            </div>
                            {field.value && (
                              <div className='space-y-1 rounded-lg bg-muted p-3'>
                                {field.value.length < 8 && (
                                  <PasswordRequirement
                                    met={false}
                                    label={t('register.errors.passwordLength')}
                                  />
                                )}
                                {!/[A-Z]/.test(field.value) && (
                                  <PasswordRequirement
                                    met={false}
                                    label={t(
                                      'register.errors.passwordUppercase'
                                    )}
                                  />
                                )}
                                {!/[a-z]/.test(field.value) && (
                                  <PasswordRequirement
                                    met={false}
                                    label={t(
                                      'register.errors.passwordLowercase'
                                    )}
                                  />
                                )}
                                {!/[0-9]/.test(field.value) && (
                                  <PasswordRequirement
                                    met={false}
                                    label={t('register.errors.passwordNumber')}
                                  />
                                )}
                                {!/[!@#$%^&*(),.?":{}|<>]/.test(
                                  field.value
                                ) && (
                                  <PasswordRequirement
                                    met={false}
                                    label={t('register.errors.passwordSpecial')}
                                  />
                                )}
                              </div>
                            )}
                          </div>
                        </FormControl>
                        <FormMessage className='text-xs' />
                      </FormItem>
                    )}
                  />
                  <Button
                    type='submit'
                    className='h-12 w-full rounded-lg bg-primary text-primary-foreground hover:bg-primary/90'
                  >
                    {t('register.submit')}
                  </Button>
                </div>
              </form>
            </Form>
          )}

          {currentStep === 2 && (
            <Form {...formStep2}>
              <form onSubmit={formStep2.handleSubmit(handleSubmitStep2)}>
                {formStep2.formState.errors.root && (
                  <div
                    className={`mb-4 text-sm ${
                      formStep2.formState.errors.root.type === 'success'
                        ? 'text-green-600'
                        : formStep2.formState.errors.root.type === 'info'
                          ? 'text-blue-600'
                          : 'text-red-500'
                    }`}
                  >
                    {formStep2.formState.errors.root.message}
                  </div>
                )}
                <div className='grid w-full items-center gap-4'>
                  <FormField
                    control={formStep2.control}
                    name='language'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className='text-base font-medium text-foreground'>
                          {t('register.languages.title')}
                        </FormLabel>
                        <FormControl>
                          <LanguageSelector
                            onSelectLanguage={handleSelectLanguage}
                            selectedLanguage={selectedLanguage}
                            className='h-12 rounded-lg focus:ring-2 focus:ring-primary'
                          />
                        </FormControl>
                        <FormMessage className='text-xs' />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={formStep2.control}
                    name='city'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className='text-base font-medium text-foreground'>
                          {t('register.city')}
                        </FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            className='h-12 rounded-lg focus:ring-2 focus:ring-primary'
                          />
                        </FormControl>
                        <FormMessage className='text-xs' />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={formStep2.control}
                    name='ageGroup'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className='text-base font-medium text-foreground'>
                          {t('register.ageGroup')}
                        </FormLabel>
                        <FormControl>
                          <select
                            {...field}
                            className='h-12 w-full rounded-lg border border-input focus:ring-2 focus:ring-primary'
                          >
                            <option value=''>{t('register.select')}</option>
                            <option value='18-25'>18-25</option>
                            <option value='26-35'>26-35</option>
                            <option value='36-50'>36-50</option>
                            <option value='51+'>51+</option>
                          </select>
                        </FormControl>
                        <FormMessage className='text-xs' />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={formStep2.control}
                    name='acceptedPolicies'
                    render={({ field }) => (
                      <FormItem className='flex flex-col space-y-2'>
                        <div className='flex items-center space-x-2'>
                          <Checkbox
                            id='terms'
                            checked={field.value}
                            onCheckedChange={(checked) =>
                              field.onChange(!!checked)
                            }
                          />
                          <div className='px-4 py-2 text-center text-sm text-muted-foreground'>
                            {t('policies.acceptTermsPolicies.text_1')}{' '}
                            <a
                              href={'/policies?tab=privacy'}
                              target='_blank'
                              rel='noopener noreferrer'
                              className='m-0 inline cursor-pointer border-none bg-transparent p-0 text-primary underline hover:text-primary/80'
                            >
                              {t('policies.acceptTermsPolicies.policy')}{' '}
                            </a>
                            {t('policies.acceptTermsPolicies.text_2')}{' '}
                            <a
                              href={'/policies?tab=terms'}
                              target='_blank'
                              rel='noopener noreferrer'
                              className='m-0 inline cursor-pointer border-none bg-transparent p-0 text-primary underline hover:text-primary/80'
                            >
                              {t('policies.acceptTermsPolicies.termsOfUse')}
                            </a>
                            .
                          </div>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button
                    type='submit'
                    className='h-12 w-full rounded-lg bg-primary text-primary-foreground hover:bg-primary/90'
                  >
                    {t('register.submit')}
                  </Button>
                </div>
              </form>
            </Form>
          )}

          <div className='relative my-6'>
            <div className='absolute inset-0 flex items-center'>
              <div className='w-full border-t border-border'></div>
            </div>
            <div className='relative flex justify-center text-sm'>
              <span className='bg-[#F9FAFB] px-2 text-muted-foreground dark:bg-background'>
                {t('auth.or')}
              </span>
            </div>
          </div>

          <button
            className='flex h-12 w-full items-center justify-center space-x-2 rounded-lg border border-border bg-card px-6 text-base font-medium text-card-foreground transition duration-75 ease-in hover:bg-muted'
            onClick={() =>
              signIn('google', {
                callbackUrl: `/`,
                popup: true,
              })
            }
          >
            <svg
              width='24'
              height='24'
              viewBox='0 0 24 24'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M21.8055 10.0415H21V10H12V14H17.6515C16.827 16.3285 14.6115 18 12 18C8.6865 18 6 15.3135 6 12C6 8.6865 8.6865 6 12 6C13.5295 6 14.921 6.577 15.9805 7.5195L18.809 4.691C17.023 3.0265 14.634 2 12 2C6.4775 2 2 6.4775 2 12C2 17.5225 6.4775 22 12 22C17.5225 22 22 17.5225 22 12C22 11.3295 21.931 10.675 21.8055 10.0415Z'
                fill='#FBC02D'
              />
              <path
                d='M3.15302 7.3455L6.43852 9.755C7.32752 7.554 9.48052 6 12 6C13.5295 6 14.921 6.577 15.9805 7.5195L18.809 4.691C17.023 3.0265 14.634 2 12 2C8.15902 2 4.82802 4.1685 3.15302 7.3455Z'
                fill='#E53935'
              />
              <path
                d='M12 22C14.583 22 16.93 21.0115 18.7045 19.404L15.6095 16.785C14.5717 17.5742 13.3037 18.0011 12 18C9.39897 18 7.19047 16.3415 6.35847 14.027L3.09747 16.5395C4.75247 19.778 8.11347 22 12 22Z'
                fill='#4CAF50'
              />
              <path
                d='M21.8055 10.0415H21V10H12V14H17.6515C17.2571 15.1082 16.5467 16.0766 15.608 16.7855L15.6095 16.785L18.7045 19.404C18.4855 19.6025 22 17 22 12C22 11.3295 21.931 10.675 21.8055 10.0415Z'
                fill='#1565C0'
              />
            </svg>
            <span>{t('auth.continueWithGoogle')}</span>
          </button>
        </CardContent>
        <CardFooter>
          <div className='flex w-full justify-between space-x-2'>
            <span className='text-muted-foreground'>
              {t('auth.alreadyHaveAccount')}
            </span>
            <a
              href={ROUTES.LOGIN}
              className='text-primary hover:text-primary/90'
            >
              {t('auth.connect')}
            </a>
          </div>
        </CardFooter>

        {showConfirmation && credentials && (
          <ConfirmationDialog
            open={showConfirmation}
            email={credentials.email}
            password={credentials.password}
            onClose={() => setShowConfirmation(false)}
            onSuccess={handleConfirmationSuccess}
          />
        )}
      </Card>
    </>
  );
}
